import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AthleteTestsView from "@/pages/AthleteTestsView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Layers, Search, ChevronDown, ChevronRight, Sparkles, Target, Star } from "lucide-react";
import { computeResult, variation, bestOf, resultLabel, semanticsOf, hasDirection, SEMANTICS, SEMANTICS_SHORT } from "@/lib/testSemantics";
import SemanticsEditor from "@/components/SemanticsEditor";
import { AthleteSelect } from "@/components/AthleteSelect";
import TestFormDialog from "@/components/TestFormDialog";
import TestSessionsTab from "@/components/testsessions/TestSessionsTab";
import { ProtocolStatusBadges, ProtocolVersionHistory } from "@/components/ProtocolStatusBadges";
import { FencingAttemptPanel } from "@/components/FencingAttemptPanel";
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import ImportDialog from "@/components/ImportDialog";
import AIGoalDialog from "@/components/AIGoalDialog";
import { displayName, initials, sortAthletes, matchesSearch } from "@/lib/athleteName";

const TYPE_LABELS = {
  antropometria: "Antropometria",
  forza: "Forza",
  velocita: "Velocità",
  salto: "Salti",
  resistenza: "Resistenza",
  resistenza_specifica: "Resistenza specifica",
  reattivita: "Reattività",
  mobilita: "Mobilità",
  equilibrio: "Equilibrio",
  potenza: "Potenza",
  coordinazione: "Coordinazione",
  scherma: "Scherma-specifici",
  gambe_scherma: "Gambe-scherma & velocità",
  tecnico: "Valutazione tecnica (1-5)",
};
const TYPE_ORDER = ["salto","forza","gambe_scherma","velocita"];
const FASE1_TYPES = new Set(["salto","forza","gambe_scherma","velocita"]);
const TYPE_COLORS = {
  antropometria: "bg-slate-100 text-slate-700",
  velocita: "bg-blue-100 text-blue-700",
  gambe_scherma: "bg-sky-100 text-sky-800",
  salto: "bg-emerald-100 text-emerald-700",
  forza: "bg-red-100 text-red-700",
  potenza: "bg-orange-100 text-orange-700",
  reattivita: "bg-purple-100 text-purple-700",
  resistenza: "bg-amber-100 text-amber-700",
  resistenza_specifica: "bg-amber-200 text-amber-900",
  mobilita: "bg-cyan-100 text-cyan-700",
  equilibrio: "bg-teal-100 text-teal-700",
  coordinazione: "bg-indigo-100 text-indigo-700",
  scherma: "bg-zinc-900 text-white",
  tecnico: "bg-fuchsia-100 text-fuchsia-800",
};

function Numpad({ value, onChange, onEnter }) {
  const push = (d) => onChange((value || "") + d);
  const back = () => onChange((value || "").slice(0, -1));
  const clear = () => onChange("");
  const keys = ["7","8","9","4","5","6","1","2","3",".","0","⌫"];
  return (
    <div className="grid grid-cols-3 gap-2">
      {keys.map((k) => (
        <button key={k} data-testid={`numpad-${k === "⌫" ? "back" : k === "." ? "dot" : k}`} onClick={() => k === "⌫" ? back() : push(k)} className="numpad-btn tap-target-xl bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 active:bg-zinc-100">{k}</button>
      ))}
      <button data-testid="numpad-clear" onClick={clear} className="col-span-1 tap-target-xl bg-zinc-100 rounded-md text-sm">C</button>
      <button data-testid="numpad-enter" onClick={onEnter} className="col-span-2 tap-target-xl bg-black text-white rounded-md text-sm font-semibold">Registra</button>
    </div>
  );
}

const TypeBadge = ({ type }) => (
  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${TYPE_COLORS[type] || "bg-zinc-100 text-zinc-700"}`}>
    {TYPE_LABELS[type] || type}
  </span>
);

const groupByType = (tests) => {
  const g = {};
  tests.forEach((t) => { const k = t.test_type || "altro"; (g[k] = g[k] || []).push(t); });
  const ordered = {};
  TYPE_ORDER.forEach((k) => { if (g[k]) ordered[k] = g[k].sort((a,b) => a.name.localeCompare(b.name)); });
  Object.keys(g).forEach((k) => { if (!ordered[k]) ordered[k] = g[k].sort((a,b) => a.name.localeCompare(b.name)); });
  return ordered;
};

// Semantica test → lib/testSemantics.js (variazione matematica ≠ miglioramento)
const trend = variation;
const pv = (r) => r?.protocol_version || 1;

// Per-exercise analytics card
function ExerciseCard({ test, entries, athletes, showAthlete, singleAthleteId, onGoalSaved, equipmentNames, onResume }) {
  const [open, setOpen] = useState(false);
  // Confronti automatici solo tra risultati della stessa versione metodologica
  const curV = test.protocol_version || 1;
  const refV = entries.some((r) => pv(r) === curV) ? curV : Math.max(...entries.map(pv));
  const otherN = entries.filter((r) => pv(r) !== refV).length;
  const sorted = entries.filter((r) => pv(r) === refV).sort((a, b) => (a.date || "").localeCompare(b.date || "")); // asc for chart
  // Senza regola di selezione (semantica indefinita, calcolo 'best') nessun migliore: si usano solo le medie dei tentativi validi
  const noRule = (test.result_mode || "best") === "best" && bestOf([1], test) == null;
  const key = noRule ? "average" : "best";
  const values = sorted.map((r) => r[key]).filter((v) => v != null);
  const latest = sorted[sorted.length - 1];
  const previous = sorted[sorted.length - 2];
  const bestAll = bestOf(values, test);
  const avgAll = values.length ? values.reduce((s, v) => s + v, 0) / values.length : null;
  const tr = previous && latest[key] != null && previous[key] != null ? trend(latest[key], previous[key], test) : null;
  const TI = tr?.icon;
  const chartData = sorted.filter((r) => r[key] != null).map((r) => ({ d: r.date, v: r[key] }));
  // Show AI goal button only when a single athlete is filtered
  const canProposeGoal = !!singleAthleteId;

  return (
    <div data-testid={`ex-card-${test.id}`} className="bg-white border border-zinc-200 rounded-md overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <button onClick={() => setOpen(!open)} className="flex-1 min-w-0 text-left">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <TypeBadge type={test.test_type} />
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${test.category === "elite" ? "bg-black text-white" : "bg-zinc-100 text-zinc-700"}`}>{test.category}</span>
              {test.technical_rating && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-800">voto 1-5</span>}
            </div>
            <div className="font-heading font-semibold text-base leading-tight truncate">{test.name}</div>
            <div className="text-xs text-zinc-500 font-mono mt-0.5">
              Unità <strong className="text-zinc-700">{test.unit}</strong> · <span data-testid={`card-semantics-${test.id}`}>{SEMANTICS_SHORT[semanticsOf(test)]}</span> · calcolo <strong>{test.result_mode || "best"}</strong>
              {test.equipment && <> · {test.equipment}</>}
            </div>
          </button>
          <div className="flex items-center gap-1">
            {canProposeGoal && hasDirection(test) && (
              <AIGoalDialog
                athleteId={singleAthleteId}
                testId={test.id}
                testName={test.name}
                testUnit={test.unit}
                higherIsBetter={semanticsOf(test) === "HIGHER_IS_BETTER"}
                currentValue={latest.best}
                onSaved={onGoalSaved}
                trigger={<button data-testid={`goal-btn-${test.id}`} className="text-[10px] uppercase tracking-wider bg-[#0052FF]/10 text-[#0052FF] hover:bg-[#0052FF]/20 px-2 py-1 rounded flex items-center gap-1"><Sparkles className="w-3 h-3" />Obiettivo AI</button>}
              />
            )}
            {open ? <ChevronDown className="w-4 h-4 text-zinc-400 mt-1" /> : <ChevronRight className="w-4 h-4 text-zinc-400 mt-1" />}
          </div>
        </div>

        {otherN > 0 && <div data-testid={`card-version-note-${test.id}`} className="mb-2 text-[11px] bg-blue-50 border border-blue-200 text-blue-900 rounded px-2 py-1">Protocollo v{refV}: best, media e variazione calcolati solo su questa versione. {otherN} risultati di altre versioni non sono confrontati automaticamente.</div>}
        {/* Metrics grid */}
        <button onClick={() => setOpen(!open)} className="w-full text-left">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div className={`border rounded p-2 ${bestAll != null ? "bg-emerald-50 border-emerald-100" : "bg-zinc-50 border-zinc-100"}`}>
              <div className={`text-[9px] uppercase tracking-wider font-mono ${bestAll != null ? "text-emerald-700" : "text-zinc-600"}`}>Best storico</div>
              <div className={`font-mono text-lg font-bold ${bestAll != null ? "text-emerald-800" : "text-zinc-400"}`} data-testid={`card-best-${test.id}`}>{bestAll != null ? <>{bestAll.toFixed(2)} <span className="text-[10px] text-emerald-600 font-normal">{test.unit}</span></> : <span className="text-[10px] font-normal">n/d · semantica da definire</span>}</div>
            </div>
            <div className="bg-zinc-50 border border-zinc-100 rounded p-2">
              <div className="text-[9px] uppercase tracking-wider text-zinc-600 font-mono">Media storica</div>
              <div className="font-mono text-lg">{avgAll != null ? avgAll.toFixed(2) : "—"} <span className="text-[10px] text-zinc-500 font-normal">{test.unit}</span></div>
            </div>
            <div className="bg-zinc-50 border border-zinc-100 rounded p-2">
              <div className="text-[9px] uppercase tracking-wider text-zinc-600 font-mono">Ultimo</div>
              <div className="font-mono text-lg" data-testid={`card-latest-${test.id}`}>{latest[key] ?? "—"} <span className="text-[10px] text-zinc-500 font-normal">{test.unit}</span></div>
              <div className="text-[9px] text-zinc-500 font-mono">{latest.date}{noRule ? " · media tentativi" : ""}</div>
            </div>
            <div className="bg-zinc-50 border border-zinc-100 rounded p-2">
              <div className="text-[9px] uppercase tracking-wider text-zinc-600 font-mono">Precedente</div>
              <div className="font-mono text-lg">{previous?.[key] ?? "—"} <span className="text-[10px] text-zinc-500 font-normal">{previous ? test.unit : ""}</span></div>
              {tr && (
                <div className={`flex items-center gap-1 text-[10px] font-mono ${tr.color}`} data-testid={`card-variation-${test.id}`}>
                  {TI && <TI className="w-3 h-3" />}{tr.delta > 0 ? "+" : ""}{tr.pct}%
                </div>
              )}
            </div>
          </div>

          {/* Sparkline */}
          {values.length >= 2 && (
            <div className="h-16 -mx-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                  <YAxis hide domain={["auto","auto"]} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, padding: "4px 8px", fontFamily: "monospace" }}
                    formatter={(v) => [`${v} ${test.unit}`, "valore"]}
                    labelFormatter={(l) => l}
                  />
                  <Line type="monotone" dataKey="v" stroke="#0052FF" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="mt-2 flex items-center gap-2 text-[10px] text-zinc-500 font-mono">
            <span>{sorted.length} misurazioni v{refV}{otherN ? ` · ${otherN} altre versioni` : ""}</span>
            {tr && <>· <span className={tr.color}>{tr.label || "trend"}</span></>}
            {!canProposeGoal && <span className="ml-auto text-zinc-400">Filtra per un singolo atleta per generare un obiettivo AI</span>}
          </div>
        </button>
      </div>

      {open && (
        <div className="border-t border-zinc-100 bg-zinc-50/50 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2">Data</th>
                {showAthlete && <th className="text-left px-4 py-2">Atleta</th>}
                <th className="text-left px-4 py-2">Tentativi</th>
                <th className="text-left px-4 py-2">Strumento</th>
                <th className="text-right px-4 py-2">Best ({test.unit})</th>
                <th className="text-right px-4 py-2">Media ({test.unit})</th>
              </tr>
            </thead>
            <tbody>
              {[...entries].sort((a,b) => (b.date||"").localeCompare(a.date||"")).map((r, idx, arr) => {
                const a = athletes.find((x) => x.id === r.athlete_id);
                const validN = (r.attempt_details || []).length ? (r.attempt_details || []).filter((x) => x.valid).length : (r.attempts || []).length;
                const voidN = (r.attempt_details || []).filter((x) => !x.valid).length;
                const prev = arr.slice(idx + 1).find((x) => x.athlete_id === r.athlete_id);
                const protoDiff = r.planned_attempts && prev?.planned_attempts && prev.planned_attempts !== r.planned_attempts;
                const verDiff = prev && pv(prev) !== pv(r);
                return (
                  <tr key={r.id} className="border-t border-zinc-100" data-testid={`result-row-${r.id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{r.date} <span data-testid={`result-version-${r.id}`} className={`ml-1 px-1 rounded text-[10px] ${verDiff ? "bg-blue-100 text-blue-800" : "bg-zinc-100 text-zinc-600"}`} title={verDiff ? "Versione del protocollo diversa dal risultato precedente: non confrontabile automaticamente" : "Versione del protocollo"}>v{pv(r)}</span></td>
                    {showAthlete && <td className="px-4 py-2">{displayName(a)}</td>}
                    <td className="px-4 py-2 font-mono text-xs text-zinc-500">
                      {(r.attempts || []).join(" · ")}
                      {r.planned_attempts && (
                        <span className="ml-2 inline-flex items-center gap-1">
                          <span data-testid={`result-valid-count-${r.id}`} className={`px-1.5 py-0.5 rounded text-[10px] ${r.status === "partial" ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-700"}`}>{validN}/{r.planned_attempts} valide{voidN ? ` · ${voidN} ann.` : ""}{r.status === "partial" ? " · parziale" : ""}</span>
                          {protoDiff && <span data-testid={`result-proto-diff-${r.id}`} title="Numero di tentativi validi diverso dal risultato precedente: protocollo operativo differente, confronto non direttamente equivalente" className="px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-800 border border-blue-200">≠ {prev.planned_attempts} tent.</span>}
                          {r.status === "partial" && onResume && <button data-testid={`result-resume-${r.id}`} onClick={() => onResume(r)} className="px-1.5 py-0.5 rounded text-[10px] border border-zinc-300 hover:bg-zinc-50">Riprendi</button>}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-zinc-600" data-testid={`result-equipment-${r.id}`}>{equipmentNames?.[r.equipment_id] || "—"}{r.side ? ` · ${r.side}` : ""}</td>
                    <td className="px-4 py-2 font-mono text-right font-semibold" data-testid={`result-best-${r.id}`}>{noRule ? "—" : (r.best ?? "—")}</td>
                    <td className="px-4 py-2 font-mono text-right">{r.average}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function TestsRouter() {
  const { user } = useAuth();
  if (user?.role === "athlete") return <AthleteTestsView />;
  return <TestsStaff />;
}

function TestsStaff() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tests, setTests] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState("");
  const [side, setSide] = useState("");
  const [athletes, setAthletes] = useState([]);
  const [batteries, setBatteries] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [fAttempts, setFAttempts] = useState([]); // tentativi schermistici dettagliati
  const [planned, setPlanned] = useState(2);
  const [resumeId, setResumeId] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [testFormOpen, setTestFormOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [current, setCurrent] = useState("");
  const [results, setResults] = useState([]);
  const [groups, setGroups] = useState([]);
  const canEditTests = ["admin", "coach", "trainer"].includes(user?.role);

  const [batOpen, setBatOpen] = useState(false);
  const [batForm, setBatForm] = useState({ name: "", test_ids: [] });
  const [batEditingId, setBatEditingId] = useState(null);
  const [runBattery, setRunBattery] = useState(null);

  // Storico filters
  const [fAthlete, setFAthlete] = useState(searchParams.get("athlete") || "");
  const [fType, setFType] = useState("");
  const [fTest, setFTest] = useState(searchParams.get("test") || "");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fEquipment, setFEquipment] = useState("");
  const [fUnit, setFUnit] = useState("");
  const [fSearch, setFSearch] = useState("");
  const [tab, setTab] = useState(searchParams.get("tab") || "entry");

  const load = async () => {
    const [t, a, r, b, e, c] = await Promise.all([api.get("/tests"), api.get("/athletes?include_archived=1"), api.get("/test-results"), api.get("/test-batteries"), api.get("/equipment"), api.get("/coaches").catch(() => ({ data: [] }))]);
    // Tipologie esposte: Salti, Forza, Gambe scherma, Velocità
    setTests(t.data.filter((x) => !x.archived && FASE1_TYPES.has(x.test_type))); setAthletes(a.data); setResults(r.data); setBatteries(b.data); setEquipment(e.data); setCoaches(c.data || []);
  };
  useEffect(() => { load(); api.get("/groups").then((r) => setGroups(r.data || [])).catch(() => {}); }, []);
  // Strumento consigliato = solo default UI, sempre modificabile; nessun default se non definito
  useEffect(() => { const t = tests.find((x) => x.id === selectedTest); setSelectedEquipment(t?.recommended_equipment_id || ""); setSide(""); if (!resumeId) { setPlanned(t?.fencing_technical ? (t.attempts || 3) : 3); setFAttempts([]); } }, [selectedTest, tests]); // eslint-disable-line react-hooks/exhaustive-deps
  const activeEquipment = useMemo(() => equipment.filter((e) => e.is_active), [equipment]);
  const equipmentName = (id) => equipment.find((e) => e.id === id)?.name;

  const enter = () => {
    if (!current) return;
    const val = parseFloat(current);
    if (isNaN(val)) return;
    setAttempts([...attempts, val]);
    setCurrent("");
  };

  const saveFencing = async (test) => {
    if (!selectedAthlete || fAttempts.length === 0) return toast.error("Seleziona atleta e inserisci almeno un tentativo");
    const validTimes = fAttempts.filter((a) => a.valid && a.time != null).map((a) => a.time);
    const best = validTimes.length ? computeResult(validTimes, test) : null;
    const avg = validTimes.length ? parseFloat((validTimes.reduce((a, b) => a + b, 0) / validTimes.length).toFixed(2)) : null;
    const status = validTimes.length >= planned ? "complete" : "partial";
    const body = {
      test_id: test.id, test_version: test.version, protocol_version: test.protocol_version || 1,
      athlete_id: selectedAthlete, date: resumeId ? (results.find((r) => r.id === resumeId)?.date || new Date().toISOString().slice(0, 10)) : new Date().toISOString().slice(0, 10),
      attempts: validTimes, best, average: avg, attempt_details: fAttempts, planned_attempts: planned, status,
      evaluator_id: fAttempts.find((a) => a.evaluator_id)?.evaluator_id || null, operator_id: user?.id || null,
      equipment_id: selectedEquipment || null, unit: test.unit || null, side: null,
    };
    try {
      if (resumeId) await api.put(`/test-results/${resumeId}`, body); else await api.post("/test-results", body);
    } catch (e) { const d = e.response?.data?.detail; if (e.response?.status === 409) load(); return toast.error(typeof d === "string" ? d : "Errore salvataggio risultato"); }
    toast.success(status === "complete" ? `Risultato completo · ${validTimes.length}/${planned} valide` : `Salvato parziale · ${validTimes.length}/${planned} valide — riprendibile dallo Storico`);
    setFAttempts([]); setResumeId(null); load();
  };
  const resumeResult = (r) => {
    setSelectedTest(r.test_id); setSelectedAthlete(r.athlete_id); setFAttempts(r.attempt_details || []); setPlanned(r.planned_attempts || tests.find((t) => t.id === r.test_id)?.attempts || 3); setResumeId(r.id); setTab("entry");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const save = async () => {
    const test = tests.find((t) => t.id === selectedTest);
    if (test?.fencing_technical) return saveFencing(test);
    if (!selectedTest || !selectedAthlete || attempts.length === 0) return toast.error("Seleziona test, atleta e inserisci tentativi");
    const best = computeResult(attempts, test);
    const avg = attempts.reduce((a,b) => a+b, 0) / attempts.length;
    try {
      await api.post("/test-results", {
        test_id: selectedTest, test_version: test.version, protocol_version: test.protocol_version || 1,
        athlete_id: selectedAthlete, date: new Date().toISOString().slice(0,10),
        attempts, best, average: parseFloat(avg.toFixed(2)),
        equipment_id: selectedEquipment || null, unit: test.unit || null, side: side || null,
      });
    } catch (e) { const d = e.response?.data?.detail; if (e.response?.status === 409) load(); return toast.error(typeof d === "string" ? d : "Errore salvataggio risultato"); }
    toast.success("Risultato registrato");
    setAttempts([]); load();
    if (runBattery && runBattery.battery.test_ids[runBattery.currentTestIndex] === selectedTest) {
      const nextIdx = runBattery.currentTestIndex + 1;
      if (nextIdx < runBattery.battery.test_ids.length) {
        setSelectedTest(runBattery.battery.test_ids[nextIdx]);
        setRunBattery({ ...runBattery, currentTestIndex: nextIdx });
        toast.info(`Prossimo test: ${nextIdx + 1}/${runBattery.battery.test_ids.length}`);
      } else {
        toast.success("Batteria completata!");
        setRunBattery(null);
      }
    }
  };

  const currentTest = tests.find((t) => t.id === selectedTest);
  const currentAthlete = athletes.find((a) => a.id === selectedAthlete);
  const visibleTests = currentAthlete?.monitoring_level === "elite" ? tests : tests.filter((t) => t.category === "standard");
  const groupedVisible = useMemo(() => groupByType(visibleTests), [visibleTests]);

  const prev = results
    .filter((r) => r.test_id === selectedTest && r.athlete_id === selectedAthlete && pv(r) === pv(tests.find((t) => t.id === selectedTest)))
    .sort((a,b) => (b.date||"").localeCompare(a.date||""));

  const best = attempts.length ? computeResult(attempts, currentTest) : null;
  const avg = attempts.length ? (attempts.reduce((a,b) => a+b, 0) / attempts.length) : null;
  const noRuleEntry = !!currentTest && (currentTest.result_mode || "best") === "best" && bestOf([1], currentTest) == null;
  const prevBest = noRuleEntry ? (prev[0]?.average ?? null) : (prev[0]?.best ?? null); // senza regola: media precedente, come nello Storico
  const tRes = currentTest && best != null ? trend(best, prevBest, currentTest) : null;
  const TrendIcon = tRes?.icon;

  const openNewBattery = () => { setBatForm({ name: "", test_ids: [] }); setBatEditingId(null); setBatOpen(true); };
  const openEditBattery = (b) => { setBatForm({ name: b.name, test_ids: b.test_ids || [] }); setBatEditingId(b.id); setBatOpen(true); };
  const saveBattery = async () => {
    if (!batForm.name) return toast.error("Nome richiesto");
    if (batEditingId) { await api.put(`/test-batteries/${batEditingId}`, { ...batForm, id: batEditingId }); toast.success("Batteria aggiornata"); }
    else { await api.post("/test-batteries", batForm); toast.success("Batteria creata"); }
    setBatOpen(false); load();
  };
  const deleteBattery = async (id) => { if (!window.confirm("Eliminare la batteria?")) return; await api.delete(`/test-batteries/${id}`); load(); };
  const startBattery = (b, athleteId) => {
    if (!athleteId) return toast.error("Seleziona un atleta");
    if (!b.test_ids?.length) return toast.error("Batteria vuota");
    setRunBattery({ battery: b, athlete_id: athleteId, currentTestIndex: 0 });
    setSelectedAthlete(athleteId); setSelectedTest(b.test_ids[0]); setAttempts([]);
    toast.info(`Batteria "${b.name}" avviata`);
  };

  // ---------- Storico ----------
  const equipmentOptions = useMemo(() => {
    const used = new Set(results.map((r) => r.equipment_id).filter(Boolean));
    return equipment.filter((e) => e.is_active || used.has(e.id));
  }, [equipment, results]);
  const unitOptions = useMemo(() => Array.from(new Set(tests.map((t) => t.unit).filter(Boolean))).sort(), [tests]);

  const filteredResults = useMemo(() => {
    return results.filter((r) => {
      const test = tests.find((t) => t.id === r.test_id);
      if (!test) return false;
      if (fAthlete && r.athlete_id !== fAthlete) return false;
      if (fType && test.test_type !== fType) return false;
      if (fTest && test.id !== fTest) return false;
      if (fFrom && (r.date || "") < fFrom) return false;
      if (fTo && (r.date || "") > fTo) return false;
      if (fEquipment && (r.equipment_id || test.legacy_equipment_id || "__none") !== fEquipment) return false;
      if (fUnit && test.unit !== fUnit) return false;
      if (fSearch && !test.name.toLowerCase().includes(fSearch.toLowerCase())) return false;
      return true;
    });
  }, [results, tests, fAthlete, fType, fTest, fFrom, fTo, fEquipment, fUnit, fSearch]);

  const byExercise = useMemo(() => {
    const map = {};
    filteredResults.forEach((r) => { (map[r.test_id] = map[r.test_id] || []).push(r); });
    return map;
  }, [filteredResults]);

  const exercisesByType = useMemo(() => {
    const list = Object.entries(byExercise).map(([tid, entries]) => ({ test: tests.find((t) => t.id === tid), entries })).filter((x) => x.test);
    const grouped = {};
    list.forEach((it) => { (grouped[it.test.test_type || "altro"] = grouped[it.test.test_type || "altro"] || []).push(it); });
    const ordered = {};
    TYPE_ORDER.forEach((k) => { if (grouped[k]) ordered[k] = grouped[k].sort((a,b) => a.test.name.localeCompare(b.test.name)); });
    Object.keys(grouped).forEach((k) => { if (!ordered[k]) ordered[k] = grouped[k].sort((a,b) => a.test.name.localeCompare(b.test.name)); });
    return ordered;
  }, [byExercise, tests]);

  const testsInFilteredType = useMemo(() => fType ? tests.filter((t) => t.test_type === fType) : tests, [tests, fType]);
  const clearFilters = () => { setFAthlete(""); setFType(""); setFTest(""); setFFrom(""); setFTo(""); setFEquipment(""); setFUnit(""); setFSearch(""); };
  const anyFilterActive = fAthlete || fType || fTest || fFrom || fTo || fEquipment || fUnit || fSearch;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">CSP Lab</h1>
          <p className="text-zinc-500 mt-1">Registrazione risultati, libreria e analisi per singolo esercizio.</p>
        </div>
        <ImportDialog entity="tests" onDone={load} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="entry" data-testid="tab-entry">Registrazione</TabsTrigger>
          <TabsTrigger value="library" data-testid="tab-library">Libreria</TabsTrigger>
          <TabsTrigger value="batteries" data-testid="tab-batteries">Batterie</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Storico & Analisi</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessioni</TabsTrigger>
        </TabsList>

        <TabsContent value="sessions" className="pt-6">
          <TestSessionsTab canWrite={canEditTests} groups={groups} batteries={batteries} />
        </TabsContent>

        <TabsContent value="entry" className="pt-6">
          {runBattery && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-blue-700 font-mono">Batteria in corso</div>
                <div className="font-semibold">{runBattery.battery.name} · Test {runBattery.currentTestIndex + 1}/{runBattery.battery.test_ids.length}</div>
              </div>
              <Button variant="outline" onClick={() => setRunBattery(null)}>Interrompi</Button>
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label>Atleta</Label>
                <AthleteSelect
                  value={selectedAthlete || ""}
                  onChange={setSelectedAthlete}
                  athletes={athletes}
                  testid="select-athlete"
                  placeholder="Seleziona atleta"
                />
              </div>
              <div>
                <Label>Test (raggruppati per tipologia)</Label>
                <Select value={selectedTest || ""} onValueChange={(v) => { setSelectedTest(v); setAttempts([]); }}>
                  <SelectTrigger data-testid="select-test" className="h-12"><SelectValue placeholder="Seleziona test" /></SelectTrigger>
                  <SelectContent className="max-h-96">
                    {Object.entries(groupedVisible).map(([type, list]) => (
                      <SelectGroup key={type}>
                        <SelectLabel className="text-[10px] uppercase tracking-wider text-zinc-400">{TYPE_LABELS[type] || type}</SelectLabel>
                        {list.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} <span className="text-zinc-500 font-mono">({t.unit})</span></SelectItem>)}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {currentTest && (
                <div className="bg-white border border-zinc-200 rounded-md p-4 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <TypeBadge type={currentTest.test_type} />
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${currentTest.category === "elite" ? "bg-black text-white" : "bg-zinc-100 text-zinc-700"}`}>{currentTest.category}</span>
                    {currentTest.technical_rating && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-fuchsia-100 text-fuchsia-800">voto 1-5</span>}
                    <span className="font-mono text-[10px] text-zinc-400" data-testid="entry-protocol-version">protocollo v{currentTest.protocol_version || 1}</span>
                  </div>
                  <div className="text-sm">
                    Unità: <span className="font-mono font-semibold">{currentTest.unit}</span> · Tentativi: {currentTest.attempts} · Calcolo: <span className="font-mono">{currentTest.result_mode || "best"}</span> · Semantica: <span data-testid="entry-semantics">{SEMANTICS.find(([k]) => k === semanticsOf(currentTest))[1]}</span>
                  </div>
                  {currentTest.equipment && <div className="text-xs text-zinc-500">Nota strumento (libreria): {currentTest.equipment}</div>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-zinc-100">
                    <div>
                      <Label className="text-xs">Strumento utilizzato {currentTest.recommended_equipment_id && <span className="text-zinc-400">· consigliato: {equipmentName(currentTest.recommended_equipment_id)}</span>}</Label>
                      <Select value={selectedEquipment || "__none"} onValueChange={(v) => setSelectedEquipment(v === "__none" ? "" : v)}>
                        <SelectTrigger data-testid="select-equipment" className="h-10"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">— Nessuno / non registrato —</SelectItem>
                          {activeEquipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{(currentTest.compatible_equipment_ids || []).includes(e.id) ? " ✓" : ""}<span className="text-zinc-400 text-xs"> · {e.kind}</span></SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {currentTest.side_applicable && (
                      <div>
                        <Label className="text-xs">Lato</Label>
                        <Select value={side || "__none"} onValueChange={(v) => setSide(v === "__none" ? "" : v)}>
                          <SelectTrigger data-testid="select-side" className="h-10"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent><SelectItem value="__none">— Non pertinente —</SelectItem><SelectItem value="bilaterale">Bilaterale</SelectItem><SelectItem value="dx">Destro</SelectItem><SelectItem value="sx">Sinistro</SelectItem></SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white border border-zinc-200 rounded-md p-3">
                  <div className="text-xs uppercase tracking-wider text-zinc-500">{resultLabel(currentTest)}</div>
                  <div className="font-mono text-xl">{best != null ? best.toFixed(2) : "—"}<span className="text-xs text-zinc-500 ml-1">{currentTest?.unit || ""}</span></div>
                </div>
                <div className="bg-white border border-zinc-200 rounded-md p-3">
                  <div className="text-xs uppercase tracking-wider text-zinc-500">Media tentativi</div>
                  <div className="font-mono text-xl">{avg != null ? avg.toFixed(2) : "—"}<span className="text-xs text-zinc-500 ml-1">{currentTest?.unit || ""}</span></div>
                </div>
                <div className="bg-white border border-zinc-200 rounded-md p-3">
                  <div className="text-xs uppercase tracking-wider text-zinc-500">Precedente</div>
                  <div className="flex items-baseline gap-1">
                    <div className="font-mono text-xl">{prevBest ?? "—"}<span className="text-xs text-zinc-500 ml-1">{currentTest?.unit || ""}</span></div>
                    {tRes && TrendIcon && <TrendIcon className={`w-4 h-4 ${tRes.color}`} />}
                  </div>
                  {tRes && tRes.pct != null && <div className={`text-[10px] font-mono ${tRes.color}`}>{tRes.delta > 0 ? "+" : ""}{tRes.pct}%</div>}
                </div>
              </div>
              {currentTest?.fencing_technical ? (
                <FencingAttemptPanel test={currentTest} coaches={coaches} attempts={fAttempts} onChange={setFAttempts} planned={planned} onPlannedChange={setPlanned} resumeOf={resumeId} equipmentId={selectedEquipment} operatorId={user?.id} />
              ) : (
              <div>
                <Label>Tentativi registrati</Label>
                <div className="flex flex-wrap gap-2 mt-2 min-h-[40px]">
                  {attempts.map((v, i) => (
                    <span key={i} className="px-3 py-2 bg-black text-white font-mono rounded-md">{v}<span className="text-white/50 ml-1 text-xs">{currentTest?.unit || ""}</span></span>
                  ))}
                </div>
              </div>
              )}
              <Button data-testid="save-result" onClick={save} className="w-full h-12 bg-black hover:bg-zinc-800">Salva risultato</Button>
            </div>

            {!currentTest?.fencing_technical && (
            <div className="space-y-3">
              <div className="bg-white border border-zinc-200 rounded-md p-4">
                <div className="text-xs uppercase tracking-wider text-zinc-500">Valore attuale <span className="text-zinc-400 normal-case">{currentTest?.unit ? `(${currentTest.unit})` : ""}</span></div>
                <div className="font-mono text-5xl tabular mt-1">{current || "0"}</div>
              </div>
              <Numpad value={current} onChange={setCurrent} onEnter={enter} />
            </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="library" className="pt-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-zinc-500">Libreria completa raggruppata per tipologia. Ogni tipologia usa unità di misura coerenti.</p>
            {canEditTests && <Button data-testid="new-test-btn" onClick={() => { setEditingTest(null); setTestFormOpen(true); }} className="bg-black hover:bg-zinc-800"><Plus className="w-4 h-4 mr-1" />Nuovo test</Button>}
          </div>
          <TestFormDialog open={testFormOpen} onOpenChange={setTestFormOpen} test={editingTest} equipment={equipment} onSaved={load} usedCount={editingTest ? results.filter((r) => r.test_id === editingTest.id).length : 0} />
          {Object.entries(groupByType(tests)).map(([type, list]) => (
            <div key={type} data-testid={`lib-type-${type}`} className="bg-white border border-zinc-200 rounded-md overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-3">
                <TypeBadge type={type} />
                <span className="font-heading font-semibold">{TYPE_LABELS[type] || type}</span>
                <span className="text-xs text-zinc-500 font-mono">{list.length} test</span>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
                {list.map((t) => (
                  <div key={t.id} data-testid={`lib-card-${t.id}`} className="border border-zinc-200 rounded-md p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${t.category === "elite" ? "bg-black text-white" : "bg-zinc-100 text-zinc-700"}`}>{t.category}</span>
                      <span className="font-mono text-[10px] text-zinc-400">{t.result_mode || "best"}</span>
                    </div>
                    <div className="font-medium text-sm flex items-center justify-between gap-2">{t.name}
                      {canEditTests && <button data-testid={`edit-test-${t.id}`} onClick={() => { setEditingTest(t); setTestFormOpen(true); }} className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-black">modifica</button>}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500" data-testid={`test-protocol-line-${t.id}`}>{t.protocol_code ? `${t.protocol_code} · ` : ""}protocollo v{t.protocol_version || 1}{t.fencing_technical ? " · voto tecnico" : ""}{t.distance_configurable ? " · distanza da definire" : t.distance_m ? ` · ${t.distance_m} m` : ""}</div>
                    <ProtocolStatusBadges test={t} />
                    <ProtocolVersionHistory test={t} />
                    <div className="text-xs text-zinc-500 mt-1 font-mono">{t.unit} · {t.attempts} tent.</div>
                    <SemanticsEditor test={t} canEdit={["admin", "coach"].includes(user?.role)} onSaved={load} />
                    {(t.recommended_equipment_id || t.equipment) && <div className="text-[10px] text-zinc-400 mt-0.5">{t.recommended_equipment_id ? `Consigliato: ${equipmentName(t.recommended_equipment_id) || t.recommended_equipment_id}` : `Nota: ${t.equipment}`}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="batteries" className="pt-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-500">Crea batterie riutilizzabili (inizio stagione, invernale, primaverile, fine stagione).</p>
            <Button data-testid="new-battery-btn" onClick={openNewBattery} className="h-11 bg-black hover:bg-zinc-800"><Plus className="w-4 h-4 mr-2" />Nuova batteria</Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...batteries].sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0) || a.name.localeCompare(b.name)).map((b) => (
              <div key={b.id} data-testid={`battery-${b.id}`} className="bg-white border border-zinc-200 rounded-md p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-zinc-400" />
                    <h3 className="font-heading font-semibold">{b.name}</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`battery-fav-${b.id}`} disabled={!["admin", "coach"].includes(user?.role)} aria-pressed={!!b.favorite} aria-label={b.favorite ? "Rimuovi dalle preferite del club" : "Segna come preferita del club"} title={b.favorite ? "Preferita del club (non implica approvazione DT)" : "Segna come preferita del club"}
                      onClick={async () => { try { await api.put(`/test-batteries/${b.id}/favorite`, { favorite: !b.favorite }); load(); } catch { toast.error("Solo admin e coach possono modificare le preferite"); } }}
                      className="p-1 disabled:cursor-default"><Star className={`w-4 h-4 ${b.favorite ? "fill-amber-400 text-amber-500" : "text-zinc-300"}`} /></button>
                    <button onClick={() => openEditBattery(b)} className="p-1 text-zinc-500 hover:text-black text-xs">Modifica</button>
                    <button onClick={() => deleteBattery(b.id)} className="p-1 text-zinc-500 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
                <div className="text-xs text-zinc-500 mb-3 font-mono">{b.test_ids?.length || 0} test</div>
                <div className="flex flex-wrap gap-1 mb-4">
                  {(b.test_ids || []).slice(0, 6).map((tid) => {
                    const t = tests.find((x) => x.id === tid);
                    return <span key={tid} className="text-[10px] px-2 py-1 bg-zinc-100 rounded">{t?.name || "?"}</span>;
                  })}
                  {(b.test_ids || []).length > 6 && <span className="text-[10px] px-2 py-1 bg-zinc-100 rounded">+{b.test_ids.length - 6}</span>}
                </div>
                <div className="flex gap-2">
                  <Select onValueChange={(v) => startBattery(b, v)}>
                    <SelectTrigger data-testid={`start-battery-${b.id}`} className="h-9 text-xs"><SelectValue placeholder="Avvia su atleta…" /></SelectTrigger>
                    <SelectContent>{athletes.map((a) => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            ))}
            {batteries.length === 0 && <div className="col-span-full text-zinc-500 text-center py-8">Nessuna batteria creata.</div>}
          </div>
        </TabsContent>

        <TabsContent value="history" className="pt-6 space-y-4">
          {/* Filtri */}
          <div className="bg-white border border-zinc-200 rounded-md p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-heading font-semibold text-sm">Filtri</div>
              {anyFilterActive && <Button size="sm" variant="outline" onClick={clearFilters} data-testid="clear-filters">Reset filtri</Button>}
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Atleta</Label>
                <Select value={fAthlete || "all"} onValueChange={(v) => setFAthlete(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="f-athlete" className="h-9"><SelectValue placeholder="Tutti" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutti</SelectItem>{athletes.map((a) => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Categoria (tipologia)</Label>
                <Select value={fType || "all"} onValueChange={(v) => { setFType(v === "all" ? "" : v); setFTest(""); }}>
                  <SelectTrigger data-testid="f-type" className="h-9"><SelectValue placeholder="Tutte" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutte le tipologie</SelectItem>{TYPE_ORDER.map((k) => <SelectItem key={k} value={k}>{TYPE_LABELS[k]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Esercizio</Label>
                <Select value={fTest || "all"} onValueChange={(v) => setFTest(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="f-test" className="h-9"><SelectValue placeholder="Tutti" /></SelectTrigger>
                  <SelectContent className="max-h-72"><SelectItem value="all">Tutti gli esercizi</SelectItem>{testsInFilteredType.sort((a,b) => a.name.localeCompare(b.name)).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Unità di misura</Label>
                <Select value={fUnit || "all"} onValueChange={(v) => setFUnit(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="f-unit" className="h-9"><SelectValue placeholder="Tutte" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutte</SelectItem>{unitOptions.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Strumento</Label>
                <Select value={fEquipment || "all"} onValueChange={(v) => setFEquipment(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="f-equipment" className="h-9"><SelectValue placeholder="Tutti" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="__none">Non registrato</SelectItem>{equipmentOptions.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{!e.is_active ? " (legacy)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Dal</Label>
                <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="f-from" className="h-9" />
              </div>
              <div>
                <Label className="text-xs">Al</Label>
                <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="f-to" className="h-9" />
              </div>
              <div>
                <Label className="text-xs">Cerca</Label>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-zinc-400" />
                  <Input placeholder="Nome esercizio…" value={fSearch} onChange={(e) => setFSearch(e.target.value)} data-testid="f-search" className="h-9 pl-8" />
                </div>
              </div>
            </div>
          </div>

          {Object.keys(exercisesByType).length === 0 ? (
            <div className="bg-white border border-zinc-200 rounded-md p-10 text-center text-zinc-500">
              {anyFilterActive ? "Nessun risultato con i filtri correnti." : "Nessun risultato registrato."}
            </div>
          ) : (
            Object.entries(exercisesByType).map(([type, list]) => (
              <div key={type} data-testid={`history-type-${type}`} className="space-y-3">
                <div className="flex items-center gap-2 pt-2">
                  <TypeBadge type={type} />
                  <h2 className="font-heading font-semibold text-lg">{TYPE_LABELS[type] || type}</h2>
                  <span className="text-xs text-zinc-500 font-mono">{list.length} esercizi</span>
                </div>
                <div className="grid lg:grid-cols-2 gap-3">
                  {list.map(({ test, entries }) => (
                    <ExerciseCard key={test.id} test={test} entries={entries} athletes={athletes} showAthlete={!fAthlete} singleAthleteId={fAthlete} onGoalSaved={load} onResume={resumeResult} equipmentNames={Object.fromEntries(equipment.map((e) => [e.id, e.name]))} />
                  ))}
                </div>
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={batOpen} onOpenChange={setBatOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{batEditingId ? "Modifica batteria" : "Nuova batteria"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Nome</Label><Input data-testid="battery-name" value={batForm.name} onChange={(e) => setBatForm({...batForm, name: e.target.value})} placeholder="es. Inizio stagione" /></div>
            <div>
              <Label>Test inclusi ({batForm.test_ids.length}) · raggruppati per tipologia</Label>
              <div className="max-h-72 overflow-y-auto no-scrollbar border border-zinc-200 rounded-md p-2 space-y-2 mt-1">
                {Object.entries(groupByType(tests)).map(([type, list]) => (
                  <div key={type}>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-400 px-2 py-1 sticky top-0 bg-white">{TYPE_LABELS[type]}</div>
                    {list.map((t) => (
                      <label key={t.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-zinc-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={batForm.test_ids.includes(t.id)}
                          onChange={(e) => setBatForm({...batForm, test_ids: e.target.checked ? [...batForm.test_ids, t.id] : batForm.test_ids.filter((x) => x !== t.id)})}
                        />
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${t.category === "elite" ? "bg-black text-white" : "bg-zinc-100 text-zinc-700"}`}>{t.category}</span>
                        <span className="text-sm">{t.name}</span>
                        <span className="text-xs text-zinc-500 font-mono ml-auto">{t.unit}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatOpen(false)}>Annulla</Button>
            <Button data-testid="battery-save" onClick={saveBattery} className="bg-black hover:bg-zinc-800">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
